// typescript
import React, { useEffect, useMemo, useState } from "react";
import { Localizer } from "@/main/react/Types";
import { ListPlus, Bookmark, Lock, Search, Send, Languages } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
    AjaxDiscussionRestControllerApi,
    AjaxGroupRestControllerApi,
    CreateDiscussionAddToGroupRequest,
    GroupsToAddDiscussionToDTO,
} from "@/generated-client";
import { ClientFactory } from "@/main/common/ClientFactory";
import { getSafeToastMessageFromError, getServerErrorCode } from "@/main/common/ApiUtils";
import {
    type DiscussionDraftPublishTargetCounts,
    type DraftGroupsToAddDiscussionToDTO,
} from "@/main/common/DiscussionDraftPublishTargetsApi";
import { emitGroupAddedToDiscussion, emitGroupRemovedFromDiscussion } from "@/main/common/EventRegistry";
import { toast } from "sonner";
import { Spinner } from "@/components/ui/spinner";
import DateFormatter from "@/main/common/DateFormatter";
import ProfileImage from "@/main/react/buttons/ProfileImage";
import SimpleDialog, { openSimpleDialog } from "@/main/react/Dialog/SimpleDialog";
import { showCreateGroupDialog } from "@/main/react/Dialog/UpdateGroupSettingsDialog";
import {WarningBanner} from "@/main/react/component/WarningBanner";
import { DiscussionAddToGroupRequestForm } from "@/main/react/Dialog/DiscussionAddToGroupRequestForm";
import logger from "@/lib/logger";
import { PaginatedListEmptyState } from "@/main/react/list/PaginatedListState";
import SensitiveBadge from "@/main/react/sensitive/SensitiveBadge";
import SensitiveMediaGuard from "@/main/react/sensitive/SensitiveMediaGuard";
import { getHideSensitiveMediaPreference, getSensitivePreferenceMode } from "@/main/common/SensitivePreference";
import { getDisplayUsername, hasDisplayUsername } from "@/main/common/UsernameDisplayUtils";
import AuthorAttribution from "@/main/react/component/AuthorAttribution";
import { getMaskedContentWarningCopy } from "@/main/react/sensitive/ContentWarningCopy";
import { samePrimaryLanguage } from "@/main/util/PublishLanguageMapping";
import { languageDisplayName } from "@/main/util/languageDisplayName";

/** Keystroke settle time before the draft picker re-queries the server for a narrowed page. */
const SEARCH_DEBOUNCE_MS = 250;

/**
 * Ceiling on how long the dialog waits for a pending draft autosave to land before loading its
 * bootstrap anyway. The flush only exists to make the picker *fresh*; a wedged save must never turn a
 * cosmetic staleness problem into a dialog that spins forever. Losing the race just means the picker
 * partitions against the previous language — stale, consistent with the server, and self-correcting.
 */
export const FLUSH_PENDING_SAVE_TIMEOUT_MS = 4000;

/** The server's typed rejection for "this group only accepts posts in its own language". */
const LANGUAGE_MISMATCH_ERROR_CODE = "LANGUAGE_MISMATCH";

/**
 * The server's typed rejection for "this discussion already has a submission awaiting review here".
 *
 * <p>Branch on this, never on the message. The message is localized before it is thrown, so the
 * substring check this replaced (`"pending request already exists"`) could not fire for a Spanish or
 * Portuguese reader — and did not match the server's actual English wording either, so it never fired
 * at all. The visible cost is a control that stays enabled after a refusal and a member tapping it
 * again: on 2026-08-25 five members took 17 such 400s in 12h, one of them 4 in 29 calls. Same defect,
 * same week, as the re-fetch fix in `GroupAddDiscussionToGroupDialog.submitAddToGroupRequest`.
 */
const PENDING_SUBMISSION_EXISTS_ERROR_CODE = "PENDING_SUBMISSION_EXISTS";

type SharedProps = {
    onClose?: () => void;
    onCreateGroup?: () => void;
    localizer: Localizer;
    /**
     * First-paint seed only, and only on the draft-publish path. The gate input is the
     * `discussionLanguageTag` the modal bootstrap returns (the draft's *persisted* language), because
     * that is what the server validates against. Gating on the language being typed is what made the
     * picker offer groups the write then rejected — see
     * `designs/01-now/2026-07-24-draft-group-language-gate-split-brain-design.md`.
     */
    discussionLanguageTag?: string;
};

type LiveDiscussionProps = SharedProps & {
    mode?: "liveDiscussion";
    discussionPublicId: string;
    isPrivateDiscussion?: boolean;
    isDiscussionAuthor?: boolean;
};

type DraftPublishTargetProps = SharedProps & {
    mode: "draftPublishTargets";
    draftPublicId: string;
    onPublishTargetCountsChange?: (counts: DiscussionDraftPublishTargetCounts) => void;
    /**
     * Persists any pending draft edit before the picker reads the draft. The editor autosaves on a 15s
     * debounce, so without this the bootstrap can return a language up to 15 seconds out of date and
     * the picker partitions against it. Best-effort by contract: it may reject or never settle, and the
     * dialog loads regardless (see FLUSH_PENDING_SAVE_TIMEOUT_MS).
     */
    flushPendingSave?: () => Promise<unknown>;
};

type Props = LiveDiscussionProps | DraftPublishTargetProps;
type GroupEntry = GroupsToAddDiscussionToDTO | DraftGroupsToAddDiscussionToDTO;

/**
 * `isSubmissionPending` on the picker projection: does the VIEWING member already have a submission
 * for this discussion awaiting leader review in this group.
 *
 * <p>Declared here rather than read off the generated client because that client is a committed
 * artifact refreshed by `make sync-client-local` (which needs a running backend), never by
 * `make build` — and rule 7 forbids hand-editing it. Delete this alias and read the field directly
 * once the client has been regenerated; the backend side is `GroupsToAddDiscussionToDTO`.
 */
type GroupEntryWithSubmissionState = GroupEntry & { isSubmissionPending?: boolean };

function isDraftPublishTargetMode(props: Props): props is DraftPublishTargetProps {
    return props.mode === "draftPublishTargets";
}

export function AddDiscussionToGroupsDialogInner(props: Props) {
    const {
        onCreateGroup,
        localizer,
        discussionLanguageTag,
    } = props;
    const isDraftMode = isDraftPublishTargetMode(props);
    // ONE language, from the server, in both modes: the value the modal bootstrap returns. That is the
    // same value the write validates against, so the picker and the write cannot disagree. In draft
    // mode the caller's prop only seeds the first paint (the list is behind the spinner until the
    // bootstrap lands) and is replaced — including being cleared — by whatever the server reports.
    const [serverDiscussionLanguageTag, setServerDiscussionLanguageTag] = useState<string | undefined>(
        isDraftMode ? discussionLanguageTag : undefined,
    );
    const effectiveDiscussionLanguageTag = serverDiscussionLanguageTag;
    const languageGateActive = Boolean(effectiveDiscussionLanguageTag && effectiveDiscussionLanguageTag.trim());
    const discussionPublicId = !isDraftMode ? props.discussionPublicId : undefined;
    const draftPublicId = isDraftMode ? props.draftPublicId : undefined;
    const flushPendingSave = isDraftMode ? props.flushPendingSave : undefined;
    const isPrivateDiscussion = !isDraftMode ? props.isPrivateDiscussion : false;
    const isDiscussionAuthor = !isDraftMode ? props.isDiscussionAuthor : false;
    const [loading, setLoading] = useState(true);
    const [leaderGroups, setLeaderGroups] = useState<GroupEntry[] | null>(null);
    const [memberGroups, setMemberGroups] = useState<GroupEntry[] | null>(null);
    const [tab, setTab] = useState<"leader" | "member">("leader");
    const [query, setQuery] = useState("");
    const [togglingIds, setTogglingIds] = useState<Set<string>>(new Set());
    const [pendingRequestGroupIds, setPendingRequestGroupIds] = useState<Set<string>>(new Set());
    const [pendingLeaderReviewGroupIds, setPendingLeaderReviewGroupIds] = useState<Set<string>>(new Set());
    const [requestTargetGroup, setRequestTargetGroup] = useState<GroupsToAddDiscussionToDTO | null>(null);
    const [requestNote, setRequestNote] = useState("");
    const [submittingRequest, setSubmittingRequest] = useState(false);

    const apiClient: AjaxGroupRestControllerApi = useMemo(
        () => ClientFactory.getAjaxGroupRestControllerApi(),
        []
    );
    const modalClient: AjaxDiscussionRestControllerApi = useMemo(
        () => ClientFactory.getAjaxDiscussionRestControllerApi(),
        []
    );
    const draftPublishTargetsApi = useMemo(
        () => ClientFactory.getDiscussionDraftPublishTargetsApi(),
        []
    );
    const requiresAuthorApproval = !isDraftMode && Boolean(isPrivateDiscussion) && !Boolean(isDiscussionAuthor);

    /**
     * Awaits a pending draft autosave, but never longer than FLUSH_PENDING_SAVE_TIMEOUT_MS and never
     * fatally: both a rejection and a hang fall through to loading the bootstrap. Resolves to nothing —
     * callers only care that it settled.
     */
    const flushPendingSaveBestEffort = React.useCallback(async (): Promise<void> => {
        if (!flushPendingSave) return;

        let timer: number | undefined;
        try {
            await Promise.race([
                Promise.resolve().then(flushPendingSave),
                new Promise<void>((resolve) => {
                    timer = window.setTimeout(resolve, FLUSH_PENDING_SAVE_TIMEOUT_MS);
                }),
            ]);
        } catch (err) {
            // A failed save leaves the picker gating on the previous persisted language: stale, still
            // consistent with what the server will validate, and corrected on the next open.
            logger.error("Failed flushing pending draft save before opening the group picker", err);
        } finally {
            if (timer !== undefined) window.clearTimeout(timer);
        }
    }, [flushPendingSave]);

    const loadAvailableGroups = React.useCallback(async (searchQuery?: string): Promise<{ leader: GroupEntry[]; member: GroupEntry[]; discussionLanguageTag?: string }> => {
        if (isDraftMode) {
            const { data } = await ClientFactory.getDraftGroupsModalBootstrap(draftPublicId ?? "", searchQuery);
            return {
                leader: data?.leaderGroups ?? [],
                member: data?.memberGroups ?? [],
                discussionLanguageTag: data?.discussionLanguageTag ?? undefined,
            };
        }

        const { data } = await ClientFactory.getDiscussionGroupsModalBootstrap(discussionPublicId ?? "");
        return {
            leader: data?.leaderGroups ?? [],
            member: data?.memberGroups ?? [],
            discussionLanguageTag: data?.discussionLanguageTag ?? undefined,
        };
    }, [discussionPublicId, draftPublicId, isDraftMode]);

    const applyLoadedGroups = React.useCallback((leader: GroupEntry[], member: GroupEntry[]) => {
        setLeaderGroups(leader);
        setMemberGroups(member);

        if (leader.length > 0) {
            setTab("leader");
        } else if (member.length > 0) {
            setTab("member");
        }
    }, []);

    /**
     * Re-reads the bootstrap and re-partitions against the server's answer, leaving the author's tab and
     * search alone. Used when a write disagrees with what the picker is showing: the UI converges on the
     * truth rather than leaving a control that looks toggleable but is not.
     */
    const refreshGroupsFromServer = React.useCallback(async (searchQuery?: string) => {
        try {
            const { leader, member, discussionLanguageTag: loadedLanguageTag } = await loadAvailableGroups(searchQuery);
            setServerDiscussionLanguageTag(loadedLanguageTag);
            setLeaderGroups(leader);
            setMemberGroups(member);
        } catch (err) {
            // Nothing user-facing: the picker keeps showing what it had, which is what it would have
            // shown anyway had we never tried.
            logger.error("Failed refreshing groups after a rejected publish target", err);
        }
    }, [loadAvailableGroups]);

    useEffect(() => {
        let mounted = true;
        setLoading(true);
        setTab("leader");
        setQuery("");
        setPendingRequestGroupIds(new Set());
        setPendingLeaderReviewGroupIds(new Set());
        setRequestTargetGroup(null);
        setRequestNote("");
        setSubmittingRequest(false);

        // Flush BEFORE the bootstrap, never in parallel: the whole point is that the read observes the
        // author's latest language. The dialog is already showing its spinner while this runs.
        flushPendingSaveBestEffort()
            // Arrow, NOT a bare reference: `.then(loadAvailableGroups)` would hand the flush's resolved
            // value to the function's first parameter, which is now the search term.
            .then(() => loadAvailableGroups())
            .then(({ leader, member, discussionLanguageTag: loadedLanguageTag }) => {
                if (!mounted) return;
                setServerDiscussionLanguageTag(loadedLanguageTag);
                applyLoadedGroups(leader, member);
            })
            .catch((err) => {
                logger.error("Failed loading groups JSON", err);
                toast.error(localizer("groups.modal.toast_error"));
            })
            .finally(() => {
                if (mounted) setLoading(false);
            });

        return () => {
            mounted = false;
        };
    }, [applyLoadedGroups, flushPendingSaveBestEffort, loadAvailableGroups, localizer]);

    const startToggling = (id: string) => setTogglingIds((prev) => new Set(prev).add(id));
    const stopToggling = (id: string) =>
        setTogglingIds((prev) => {
            const next = new Set(prev);
            next.delete(id);
            return next;
        });

    const updateGroupLists = (groupPublicId: string, updater: (group: GroupEntry) => GroupEntry) => {
        const update = (prev: GroupEntry[] | null) => {
            if (!prev) return prev;
            return prev.map((g) =>
                g.publicId === groupPublicId
                    ? updater(g)
                    : g
            );
        };

        setLeaderGroups(update);
        setMemberGroups(update);
    };

    const markPendingRequest = (groupPublicId: string) => {
        setPendingRequestGroupIds((prev) => {
            if (prev.has(groupPublicId)) return prev;
            const next = new Set(prev);
            next.add(groupPublicId);
            return next;
        });
    };

    const clearPendingRequest = (groupPublicId: string) => {
        setPendingRequestGroupIds((prev) => {
            if (!prev.has(groupPublicId)) return prev;
            const next = new Set(prev);
            next.delete(groupPublicId);
            return next;
        });
    };

    const markPendingLeaderReview = (groupPublicId: string) => {
        setPendingLeaderReviewGroupIds((prev) => {
            if (prev.has(groupPublicId)) return prev;
            const next = new Set(prev);
            next.add(groupPublicId);
            return next;
        });
    };

    const clearPendingLeaderReview = (groupPublicId: string) => {
        setPendingLeaderReviewGroupIds((prev) => {
            if (!prev.has(groupPublicId)) return prev;
            const next = new Set(prev);
            next.delete(groupPublicId);
            return next;
        });
    };

    async function createAddToGroupRequest(groupPublicId: string, note?: string) {
        const trimmedNote = note?.trim();
        const payload: CreateDiscussionAddToGroupRequest = trimmedNote ? { note: trimmedNote } : {};

        await modalClient.createAddToGroupRequest(
            discussionPublicId!,
            groupPublicId,
            payload
        );
    }

    function openRequestForm(group: GroupsToAddDiscussionToDTO) {
        setRequestTargetGroup(group);
        setRequestNote("");
    }

    function closeRequestForm() {
        if (submittingRequest) return;
        setRequestTargetGroup(null);
        setRequestNote("");
    }

    async function submitAddToGroupRequest() {
        if (!requestTargetGroup?.publicId || submittingRequest) return;

        setSubmittingRequest(true);

        try {
            await createAddToGroupRequest(requestTargetGroup.publicId, requestNote);
            markPendingRequest(requestTargetGroup.publicId);
            setRequestTargetGroup(null);
            setRequestNote("");
            toast.success(localizer("discussion.request.modal.request_sent"));
        } catch (err) {
            logger.error("Failed creating add-to-group request", err);
            const message = getSafeToastMessageFromError(
                err,
                localizer("groups.modal.toast_error")
            );
            if (getServerErrorCode(err) === PENDING_SUBMISSION_EXISTS_ERROR_CODE) {
                markPendingRequest(requestTargetGroup.publicId);
                setRequestTargetGroup(null);
                setRequestNote("");
            }
            toast.error(message);
        } finally {
            setSubmittingRequest(false);
        }
    }

    async function toggleGroup(group: GroupsToAddDiscussionToDTO) {
        if (loading || !group.publicId) return;

        if (group.isDiscussionExcluded) {
            return;
        }

        const isActive = Boolean(group.isDiscussionAdded);
        const groupPublicId = group.publicId;
        const hasPendingRequest = pendingRequestGroupIds.has(groupPublicId);

        if (togglingIds.has(groupPublicId) || hasPendingRequest) return;

        startToggling(groupPublicId);

        try {
            if (isActive) {
                await apiClient.removeDiscussionFromGroup(groupPublicId, discussionPublicId!);
                clearPendingRequest(groupPublicId);
                clearPendingLeaderReview(groupPublicId);
            } else {
                const { data } = await ClientFactory.addDiscussionToGroupWithResult(groupPublicId, discussionPublicId!);
                clearPendingRequest(groupPublicId);
                const addedAfterSave = data?.status === "ADDED" || data?.status === "ALREADY_ADDED";

                if (!addedAfterSave) {
                    markPendingLeaderReview(groupPublicId);
                    toast.success(localizer("group.discussion_submission.request_sent"));
                    return;
                }
                clearPendingLeaderReview(groupPublicId);
            }

            updateGroupLists(groupPublicId, (entry) => ({ ...entry, isDiscussionAdded: !isActive }));

            const updatedGroup = {
                ...group,
                isDiscussionAdded: !isActive,
            };

            if (!isActive) {
                emitGroupAddedToDiscussion({ discussionPublicId: discussionPublicId!, group: updatedGroup });
                toast.success(localizer("groups.modal.toast_added"));
            } else {
                emitGroupRemovedFromDiscussion({ discussionPublicId: discussionPublicId!, group: updatedGroup });
                toast.success(localizer("groups.modal.toast_removed"));
            }
        } catch (err) {
            logger.error("Failed toggling group membership", err);
            const maybeMessage = getSafeToastMessageFromError(
                err,
                localizer("groups.modal.toast_error")
            );
            if (getServerErrorCode(err) === PENDING_SUBMISSION_EXISTS_ERROR_CODE) {
                markPendingRequest(groupPublicId);
            }
            toast.error(maybeMessage);
        } finally {
            stopToggling(groupPublicId);
        }
    }

    async function toggleDraftGroup(group: DraftGroupsToAddDiscussionToDTO) {
        if (loading || !group.publicId || !draftPublicId) return;
        if (group.isDiscussionExcluded) return;

        const groupPublicId = group.publicId;
        const alreadyAdded = Boolean(group.isDiscussionAdded);
        const selectedForPublish = Boolean(group.isSelectedForPublish);

        if (togglingIds.has(groupPublicId) || (alreadyAdded && !selectedForPublish)) {
            return;
        }

        startToggling(groupPublicId);

        try {
            const counts = selectedForPublish
                ? await draftPublishTargetsApi.removeDraftGroupTarget(draftPublicId, groupPublicId)
                : await draftPublishTargetsApi.addDraftGroupTarget(draftPublicId, groupPublicId);

            updateGroupLists(groupPublicId, (entry) => ({
                ...entry,
                isSelectedForPublish: !selectedForPublish,
            }));

            if (isDraftMode) {
                props.onPublishTargetCountsChange?.(counts);
            }
            toast.success(localizer(
                selectedForPublish
                    ? "create.discussion.publish_targets.groups.toast_removed"
                    : "create.discussion.publish_targets.groups.toast_added"
            ));
        } catch (err) {
            // The picker and the write can still disagree — a save that failed, or the draft edited in
            // another tab. Converge on the server's answer instead of complaining: re-read the bootstrap
            // and re-partition, which visibly moves this group into the "other languages" section that
            // already explains itself. Correcting the list silently beats a red toast about an
            // inconsistency we just fixed (AGENTS rule 12).
            if (getServerErrorCode(err) === LANGUAGE_MISMATCH_ERROR_CODE) {
                logger.warn("Draft group target rejected as a language mismatch; re-reading the picker state");
                await refreshGroupsFromServer();
                return;
            }

            logger.error("Failed toggling draft group publish target", err);
            toast.error(getSafeToastMessageFromError(
                err,
                localizer("create.discussion.publish_targets.groups.toast_error"),
            ));
        } finally {
            stopToggling(groupPublicId);
        }
    }

    async function handleGroupAction(group: GroupsToAddDiscussionToDTO) {
        if (isDraftMode) {
            await toggleDraftGroup(group as DraftGroupsToAddDiscussionToDTO);
            return;
        }

        if (loading || !group.publicId) return;

        if (group.isDiscussionExcluded) {
            return;
        }

        const isActive = Boolean(group.isDiscussionAdded);
        const hasPendingRequest = pendingRequestGroupIds.has(group.publicId);

        if (togglingIds.has(group.publicId) || hasPendingRequest || submittingRequest) return;

        if (requiresAuthorApproval && !isActive) {
            openRequestForm(group);
            return;
        }

        await toggleGroup(group);
    }

    const normalizedQuery = useMemo(() => query.trim().toLowerCase(), [query]);
    const filterGroups = React.useCallback((items: GroupEntry[] | null) => {
        if (!items) return items;
        if (!normalizedQuery) return items;

        return items.filter((group) => {
            const haystack = [
                group.name,
                group.description,
                group.creatorUsername,
            ]
                .filter(Boolean)
                .join(" ")
                .toLowerCase();

            return haystack.includes(normalizedQuery);
        });
    }, [normalizedQuery]);

    /**
     * The draft picker's search runs on the SERVER (2026-08-22).
     *
     * <p>Each list is capped at 50 groups ordered by NAME, so a group outside the first page is simply
     * not in the payload and no amount of client-side filtering can reach it. Before the cap the whole
     * leader list shipped on every open — measured at 2322 groups for one account — which is the defect
     * the cap exists to fix; this effect is what keeps the cap from costing reachability.</p>
     *
     * <p>Guarded by a ref holding the term the server was last asked for, so: opening the dialog does
     * not double-fetch (the open effect already fetched with ""), typing fetches, and clearing the box
     * back to "" fetches again rather than leaving the narrowed page on screen. `filterGroups` still
     * runs over the result and is a no-op refinement — it tests the same
     * name/description/creator haystack the query now matches server-side.</p>
     */
    const lastServerSearchRef = React.useRef("");
    useEffect(() => {
        if (!isDraftMode) {
            return;
        }
        if (normalizedQuery === lastServerSearchRef.current) {
            return;
        }
        const handle = window.setTimeout(() => {
            lastServerSearchRef.current = normalizedQuery;
            void refreshGroupsFromServer(normalizedQuery);
        }, SEARCH_DEBOUNCE_MS);
        return () => window.clearTimeout(handle);
    }, [isDraftMode, normalizedQuery, refreshGroupsFromServer]);

    // Reset the memory of what the server was last asked for whenever the dialog reopens, so the first
    // search of a new session is not swallowed by the previous one's value.
    useEffect(() => {
        lastServerSearchRef.current = "";
    }, [draftPublicId, discussionPublicId]);

    const visibleLeaderGroups = useMemo(() => filterGroups(leaderGroups), [filterGroups, leaderGroups]);
    const visibleMemberGroups = useMemo(() => filterGroups(memberGroups), [filterGroups, memberGroups]);
    // Drives the member-tab notice. Draft mode excluded for the same reason renderGroupEntry excludes
    // it: that path never removes a post from a group.
    const hasLockedMemberRemovals = useMemo(
        () => !isDraftMode && (memberGroups ?? []).some((group) => Boolean(group.isDiscussionAdded)),
        [isDraftMode, memberGroups],
    );

    // Split a visible list into groups the post can be added to (same language) and groups shown
    // muted for explanation (a different language). No gate → everything is "matching".
    const partitionByLanguage = React.useCallback((items: GroupEntry[] | null) => {
        if (!items || !languageGateActive) {
            return { matching: items ?? [], nonMatching: [] as GroupEntry[] };
        }
        const matching: GroupEntry[] = [];
        const nonMatching: GroupEntry[] = [];
        for (const group of items) {
            if (samePrimaryLanguage((group as { mainLocale?: string | null }).mainLocale, effectiveDiscussionLanguageTag)) {
                matching.push(group);
            } else {
                nonMatching.push(group);
            }
        }
        return { matching, nonMatching };
    }, [effectiveDiscussionLanguageTag, languageGateActive]);

    // Localized display name of a group's language, for the muted "In {language}" badge. The shared
    // reader reduces `pt-BR` / `en_US` / `EN` to the primary subtag before the lookup (SV-73).
    const languageLabelFor = React.useCallback(
        (mainLocale: string | null | undefined): string => languageDisplayName(localizer, mainLocale),
        [localizer],
    );

    useEffect(() => {
        if (!normalizedQuery) return;
        if (tab === "leader" && (visibleLeaderGroups?.length ?? 0) === 0 && (visibleMemberGroups?.length ?? 0) > 0) {
            setTab("member");
            return;
        }
        if (tab === "member" && (visibleMemberGroups?.length ?? 0) === 0 && (visibleLeaderGroups?.length ?? 0) > 0) {
            setTab("leader");
        }
    }, [normalizedQuery, tab, visibleLeaderGroups, visibleMemberGroups]);

    function renderGroupEntry(group: GroupsToAddDiscussionToDTO, source: "leader" | "member") {
        const pendingRequest = Boolean(group.publicId && pendingRequestGroupIds.has(group.publicId));
        // Two sources, OR'd, and deliberately NOT synced into one another (rule 31: never keep the
        // same value in two places and write both). `isSubmissionPending` is what the SERVER loaded —
        // a submission this member raised on an earlier visit that is still awaiting review — and
        // `pendingLeaderReviewGroupIds` is what THIS dialog session has added since. Before the server
        // field existed the set was the only source, so it started empty on every open and the picker
        // cheerfully re-offered a group whose submission was already queued: the member pressed it,
        // the server refused with `pending_submission_exists`, and they got an error toast for an
        // action that could never have worked (three members, six times, 2026-09-03). Nothing here
        // clears the server's answer, because nothing in this dialog can withdraw a submission;
        // `refreshGroupsFromServer` re-reads it if a leader resolves one while the dialog is open.
        const pendingLeaderReview = Boolean(
            group.publicId
            && ((group as GroupEntryWithSubmissionState).isSubmissionPending
                || pendingLeaderReviewGroupIds.has(group.publicId)),
        );
        const draftGroup = group as DraftGroupsToAddDiscussionToDTO;
        const alreadyAddedLocked = isDraftMode && Boolean(draftGroup.isDiscussionAdded) && !Boolean(draftGroup.isSelectedForPublish);
        // Only a leader may remove a post from a group; any member may add (which raises a submission
        // for review). This dialog already knows which it is — the group came from `leaderGroups` or
        // `memberGroups` — so no extra server field is needed here, unlike the group-side dialog.
        // Draft mode is excluded because it never calls removeDiscussionFromGroup: `handleGroupAction`
        // routes it to `toggleDraftGroup`, which edits publish targets, not group membership.
        const removalLocked = !isDraftMode && source === "member" && Boolean(group.isDiscussionAdded);
        const disabled = loading || Boolean(group.isDiscussionExcluded) || pendingRequest || pendingLeaderReview || alreadyAddedLocked || removalLocked;

        return (
            <GroupDialogCardDisplayer
                key={group.publicId}
                group={group}
                localizer={localizer}
                isDiscussionAdded={Boolean(group.isDiscussionAdded)}
                isSelectedForPublish={isDraftMode ? Boolean(draftGroup.isSelectedForPublish) : undefined}
                selectionMode={isDraftMode ? "draft" : "live"}
                alreadyAddedLocked={alreadyAddedLocked}
                removalLocked={removalLocked}
                hasPendingRequest={pendingRequest}
                hasPendingLeaderReview={pendingLeaderReview}
                isToggling={Boolean(group.publicId && togglingIds.has(group.publicId))}
                requestMode={requiresAuthorApproval}
                disabled={disabled}
                onToggleDiscussionAdded={async () => {
                    if (disabled) return;
                    await handleGroupAction(group);
                }}
            />
        );
    }

    function renderMismatchEntry(group: GroupEntry) {
        return (
            <GroupDialogCardDisplayer
                key={group.publicId}
                group={group}
                localizer={localizer}
                selectionMode={isDraftMode ? "draft" : "live"}
                disabled
                languageMismatchLabel={languageLabelFor((group as { mainLocale?: string | null }).mainLocale)}
            />
        );
    }

    function renderTabContent(items: GroupEntry[] | null, filteredItems: GroupEntry[] | null, source: "leader" | "member") {
        if (loading) {
            return (
                <div className="w-full flex items-center justify-center py-6">
                    <Spinner className="w-10 h-10 text-neutral" />
                </div>
            );
        }

        if (items && items.length > 0 && filteredItems && filteredItems.length > 0) {
            const { matching, nonMatching } = partitionByLanguage(filteredItems);
            return (
                <div className="flex flex-col gap-2 w-full max-h-[70vh] overflow-y-auto no-scrollbar pr-2">
                    {matching.map((group) => renderGroupEntry(group, source))}
                    {nonMatching.length > 0 ? (
                        <div
                            className="mt-2 flex flex-col gap-2 border-t border-[var(--line-color)]/60 pt-3"
                            data-playwright-id="discussion-add-to-groups-language-mismatch-section"
                        >
                            <div className="px-1">
                                <div className="text-xs font-medium text-muted-foreground">
                                    {localizer("groups.modal.language_mismatch.section_title")}
                                </div>
                                <div className="text-xs text-muted-foreground">
                                    {localizer("groups.modal.language_mismatch.section_help")}
                                </div>
                            </div>
                            {nonMatching.map((group) => renderMismatchEntry(group))}
                        </div>
                    ) : null}
                </div>
            );
        }

        if (normalizedQuery && items && items.length > 0) {
            return (
                <PaginatedListEmptyState
                    icon={Search}
                    title={localizer("groups.modal.search_empty.title")}
                    description={localizer("groups.modal.search_empty.description")}
                />
            );
        }

        const emptyTitle = source === "leader"
            ? localizer("groups.modal.empty.leader.title")
            : localizer("groups.modal.empty.member.title");
        const emptyDescription = source === "leader"
            ? localizer("groups.modal.empty.leader.description")
            : localizer("groups.modal.empty.member.description");

        return (
            <PaginatedListEmptyState
                icon={ListPlus}
                title={emptyTitle}
                description={emptyDescription}
                action={onCreateGroup
                    ? {
                        label: localizer("groups.modal.new_group"),
                        onClick: onCreateGroup,
                        icon: ListPlus,
                    }
                    : undefined}
            />
        );
    }

    const renderPrivateRequestForm = () => {
        if (!requestTargetGroup) return null;

        const requestTargetGroupWithFlags = requestTargetGroup as GroupsToAddDiscussionToDTO & { sensitive?: boolean | null };
        const isSensitive = Boolean(requestTargetGroupWithFlags.sensitive);

        return (
            <DiscussionAddToGroupRequestForm
                localizer={localizer}
                backLabel={localizer("discussion.request.modal.back")}
                targetLabel={localizer("discussion.request.modal.selected_group")}
                noteInputId="discussion-request-note"
                note={requestNote}
                submitting={submittingRequest}
                onBack={closeRequestForm}
                onCancel={closeRequestForm}
                onSubmit={submitAddToGroupRequest}
                onNoteChange={setRequestNote}
            >
                <div className="flex gap-3 items-start min-w-0">
                    {requestTargetGroup.thumbnailUrl ? (
                        <ProfileImage localizer={localizer}
                            imageUrl={requestTargetGroup.thumbnailUrl}
                            username={requestTargetGroup.name ?? ""}
                            mode="group"
                            hoverable={false}
                            className="size-12 shrink-0"
                        />
                    ) : null}

                    <div className="min-w-0 flex-1">
                        <div className="font-semibold text-base">
                            <span className="inline-flex flex-wrap items-center gap-2">
                                <span>{requestTargetGroup.name}</span>
                                {isSensitive ? <SensitiveBadge className="py-0">{localizer("content-warning.badge.sensitive")}</SensitiveBadge> : null}
                            </span>
                        </div>
                        {/* Body copy in the confirmation preview, so `text-muted-foreground` for the
                            same reason as the card row's meta line: `text-neutral` is `--neutral-color`
                            (gray-400), the border/icon tint, and it measures ~2.3:1 on this surface. */}
                        <p className="mt-1 text-sm text-muted-foreground line-clamp-3">
                            {requestTargetGroup.description ?? localizer("group.no_description")}
                        </p>
                        <div className="mt-2 text-xs text-muted-foreground">
                            {requestTargetGroup.discussionsCount ?? 0} {localizer("group.discussions")} · {requestTargetGroup.privateAccess ? localizer("group.private") : localizer("group.public")}
                        </div>
                    </div>
                </div>
            </DiscussionAddToGroupRequestForm>
        );
    };

    return (
        <div className="relative w-full">
            <div className="flex flex-col gap-4 items-start justify-start w-full">
                {requiresAuthorApproval && !requestTargetGroup ? (
                    <WarningBanner
                        icon={Lock}
                        description={localizer("discussion.request.modal.help")}
                    />
                ) : null}

                {requestTargetGroup ? renderPrivateRequestForm() : null}

                {!requestTargetGroup && (
                    <>
                        <div className="relative w-full">
                            <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-neutral" aria-hidden="true" />
                            <input
                                type="search"
                                value={query}
                                onChange={(event) => setQuery(event.target.value)}
                                placeholder={localizer("groups.modal.search_placeholder")}
                                aria-label={localizer("groups.modal.search_placeholder")}
                                className="w-full rounded-lg border border-border bg-input/70 py-3 pl-10 pr-4 text-sm [@media(pointer:coarse)]:text-base text-text-main outline-none transition-colors placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50"
                                data-playwright-id="discussion-add-to-groups-search"
                            />
                        </div>

                    <Tabs className="w-full" value={tab} onValueChange={(v) => setTab(v as "leader" | "member")}>
                        <TabsList variant={"line"} className="w-full justify-center">
                            <TabsTrigger value="leader">
                                {localizer("groups.modal.leader_section")}
                            </TabsTrigger>
                            <TabsTrigger value="member">
                                {localizer("groups.modal.member_section")}
                            </TabsTrigger>
                        </TabsList>

                        <TabsContent value="leader" className={"w-full max-w-full"}>
                            {renderTabContent(leaderGroups, visibleLeaderGroups, "leader")}
                        </TabsContent>

                        <TabsContent value="member" className={"w-full max-w-full"}>
                            {/* Only in the MEMBER tab, and only when something there is actually
                                locked: these are groups the viewer belongs to but does not lead, so a
                                post already in one can only be taken out by a leader. The leader tab
                                needs no such notice. */}
                            {hasLockedMemberRemovals ? (
                                <div className="w-full pb-2" data-playwright-id="discussion-add-to-groups-remove-locked-banner">
                                    <WarningBanner
                                        icon={Lock}
                                        description={localizer("group.discussion.remove_leader_only")}
                                    />
                                </div>
                            ) : null}
                            {renderTabContent(memberGroups, visibleMemberGroups, "member")}
                        </TabsContent>
                    </Tabs>
                    </>
                )}
            </div>
        </div>
    );
}

export function GroupDialogCardDisplayer(props: {
    group: GroupsToAddDiscussionToDTO;
    localizer: Localizer;
    isDiscussionAdded?: boolean;
    isSelectedForPublish?: boolean;
    selectionMode?: "live" | "draft";
    alreadyAddedLocked?: boolean;
    hasPendingRequest?: boolean;
    hasPendingLeaderReview?: boolean;
    isToggling?: boolean;
    requestMode?: boolean;
    disabled?: boolean;
    /**
     * The post is in this group and the viewer does not lead it, so only a leader can take it out.
     * Surfaced as `data-removal-locked` because a locked added card looks nearly identical to a
     * normal one — the spec must assert the mechanism, not the appearance (rule 20).
     */
    removalLocked?: boolean;
    languageMismatchLabel?: string;
    onToggleDiscussionAdded?: () => Promise<void> | void;
}) {
    const { group, localizer, isDiscussionAdded, isSelectedForPublish, selectionMode, alreadyAddedLocked, removalLocked, hasPendingRequest, hasPendingLeaderReview, onToggleDiscussionAdded, isToggling, requestMode, disabled, languageMismatchLabel } = props;
    const languageMismatch = Boolean(languageMismatchLabel);
    const groupWithFlags = group as GroupsToAddDiscussionToDTO & { sensitive?: boolean | null };
    const createdAt = group?.createdAt;
    const df = new DateFormatter(localizer);
    const isSensitive = Boolean(groupWithFlags.sensitive);
    const sensitivePreference = getSensitivePreferenceMode();
    const hideSensitiveMedia = getHideSensitiveMediaPreference();

    const added = Boolean(isDiscussionAdded ?? group.isDiscussionAdded);
    const selectedForPublish = Boolean(isSelectedForPublish);
    const pending = Boolean(hasPendingRequest);
    const pendingLeaderReview = Boolean(hasPendingLeaderReview);
    const excluded = Boolean(group.isDiscussionExcluded);
    const canRequest = Boolean(requestMode) && !added && !pending;
    const active = selectionMode === "draft" ? selectedForPublish : added;
    const borderClass = languageMismatch
        ? "border border-dashed border-[var(--line-color)]/60"
        : active
        ? "border border-primary"
        : pending
            ? "border border-[var(--warning-border)]"
            : pendingLeaderReview
                ? "border border-primary/40"
            : excluded
                ? "border border-border/70"
            : alreadyAddedLocked
                ? "border border-primary/40"
            : canRequest
                ? "border border-primary/40"
            // The RESTING border is the hairline token, matching `GroupAddDiscussionToGroupDialog`'s
            // ladder. The raw `--line-color` var stays only on the dashed language-mismatch state above.
            : "border border-border";
    const warningCopy = getMaskedContentWarningCopy(localizer, { isSensitive });

    const handleClick = async (e?: React.SyntheticEvent) => {
        try {
            e?.stopPropagation();
            e?.preventDefault();
        } catch {
            /* ignore */
        }

        if (!onToggleDiscussionAdded || isToggling || disabled) return;
        await onToggleDiscussionAdded();
    };

    const onKeyDown = async (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            await handleClick(e);
        }
    };

    // No `key` here on purpose: a key on the root a component returns is inert — React reads the key
    // on the element in the list. Each caller keys its own <GroupDialogCardDisplayer> at the map site.
    return (
        <div
            className={`relative w-full min-w-0 max-w-full rounded-xl p-2 outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 ${borderClass} ${disabled ? "cursor-not-allowed opacity-85" : "cursor-pointer"}`}
            onClick={handleClick}
            role="button"
            tabIndex={0}
            onKeyDown={onKeyDown}
            aria-pressed={selectionMode === "draft" ? active : added || pending}
            aria-disabled={disabled}
            data-group-public-id={group.publicId ?? undefined}
            data-language-mismatch={languageMismatch ? "true" : undefined}
            data-removal-locked={removalLocked ? "true" : undefined}
        >
            <div className="flex min-w-0 gap-2 items-start">
                {group.thumbnailUrl && group.thumbnailUrl.length > 0 ? (
                    <SensitiveMediaGuard
                        isSensitive={isSensitive}
                        hideSensitiveMedia={hideSensitiveMedia}
                        sensitivePreference={sensitivePreference}
                        variant="avatar"
                        overlayStacking="below-link-overlay"
                        badgeLabel={warningCopy.badgeLabel}
                        label={warningCopy.label}
                        helperText={warningCopy.helperText}
                        revealText={warningCopy.revealText}
                        className="rounded-full"
                    >
                        <ProfileImage localizer={localizer}
                            imageUrl={group.thumbnailUrl}
                            username={group.name ?? ""}
                            mode="group"
                            hoverable={false}
                            className={"size-12 shrink-0 sm:size-24"}
                        />
                    </SensitiveMediaGuard>
                ) : null}

                <div className="min-w-0">
                    {/* The corner slot is IN FLOW and floated, not `absolute`.
                        It holds either a 20px bookmark glyph or a status chip that is a full localized
                        sentence — "Waiting for group leaders" is ~159px in English and longer in Spanish.
                        While it was `absolute top-2 right-2` nothing reserved its width, and the fixed
                        `pe-24` gutter that stood in for that reservation was a GUESS about a localized
                        string: it reserved 96px against a chip that needs up to ~159px, so the chip
                        painted over the title in every locale, and it spent those 96px of a 229px title
                        on the majority of cards that show only the glyph. Floated, the title's first
                        line wraps around whatever width the chip actually took and later lines recover
                        the full column — nothing is estimated. `relative z-20` is kept: the slot still
                        has to sit above the sensitive-media overlay in the thumbnail column.
                        Pinned across all four pickers by `dialogCardTitleGutter.spec.tsx`. */}
                    <div className="relative z-20 float-right ms-2" data-playwright-id="picker-card-corner-slot">
                        {languageMismatch ? (
                            <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-1 text-[11px] font-medium text-muted-foreground">
                                <Languages className="size-3" aria-hidden="true" />
                                {languageMismatchLabel}
                            </span>
                        ) : isToggling ? (
                            <Spinner className="w-5 h-5 text-neutral" />
                        ) : excluded ? (
                            <span className="rounded-full bg-muted px-2 py-1 text-[11px] font-medium text-muted-foreground">
                                {localizer("group.discussion_submission.status.excluded")}
                            </span>
                        ) : alreadyAddedLocked ? (
                            <span className="rounded-full bg-primary/10 px-2 py-1 text-[11px] font-medium text-primary">
                                {localizer("create.discussion.publish_targets.already_added")}
                            </span>
                        ) : pending ? (
                            <span className="rounded-full bg-[var(--warning-icon-bg)] px-2 py-1 text-[11px] font-medium text-[var(--warning)]">
                                {localizer("discussion.request.modal.request_pending")}
                            </span>
                        ) : pendingLeaderReview ? (
                            <span className="rounded-full bg-primary/10 px-2 py-1 text-[11px] font-medium text-primary">
                                {localizer("group.discussion_submission.status.waiting_for_leaders")}
                            </span>
                        ) : canRequest ? (
                            <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-1 text-[11px] font-medium text-primary">
                                <Send className="size-3" aria-hidden="true" />
                                {localizer("discussion.request.modal.submit")}
                            </span>
                        ) : active ? (
                            <Bookmark className="w-5 h-5 text-primary fill-current stroke-0" aria-hidden="true" />
                        ) : (
                            <Bookmark className="w-5 h-5 fill-background1 text-muted" aria-hidden="true" />
                        )}
                    </div>
                    <h4 className="wrap-anywhere font-semibold text-base">
                        <span className="inline-flex flex-wrap items-center gap-2">
                            <span>{group.name}</span>
                            {isSensitive ? <SensitiveBadge className="py-0">{localizer("content-warning.badge.sensitive")}</SensitiveBadge> : null}
                        </span>
                    </h4>

                    {(hasDisplayUsername(group.creatorUsername) || createdAt) ? (
                        <AuthorAttribution localizer={localizer}
                            name={getDisplayUsername(group.creatorUsername, localizer)}
                            imageUrl={group.creatorProfileImageUrl}
                            avatarSeed={group.creatorUsername}
                            details={createdAt ? <time dateTime={createdAt}>{df.timeSince(createdAt)}</time> : null}
                            className="mt-1 text-xs"
                            avatarClassName="size-5"
                        />
                    ) : null}

                    <p className="mt-2 text-sm line-clamp-3 wrap-anywhere">
                        {group.description ?? localizer("group.no_description")}
                    </p>

                    {/* `text-muted-foreground`, not `text-neutral`: the latter is `--neutral-color`
                        (gray-400), a border/icon tint, and at 12px on the card surface it measured
                        ~2.3:1 — under the AA 4.5:1 floor for body text. */}
                    <div className="text-xs text-muted-foreground mt-2">
                        {group.discussionsCount ?? 0} {localizer("group.discussions")} ·{" "}
                        {group.privateAccess ? localizer("group.private") : localizer("group.public")}
                    </div>

                    {canRequest ? (
                        <div className="mt-2 flex items-center gap-1 text-xs text-primary">
                            <Lock className="size-3" aria-hidden="true" />
                            {localizer("discussion.request.modal.click_to_request")}
                        </div>
                    ) : null}
                </div>
            </div>
        </div>
    );
}

export function showAddDiscussionToGroupsDialog(
    discussionPublicId: string,
    localizer: Localizer,
    options?: {
        isPrivateDiscussion?: boolean;
        isDiscussionAuthor?: boolean;
    }
) {
    openSimpleDialog(({ open, onOpenChange }) => (
        <SimpleDialog
            open={open}
            onOpenChange={onOpenChange}
            title={<div id="groups-dialog-title">{localizer("groups.modal.title")}</div>}
            width="xl"
        >
            <AddDiscussionToGroupsDialogInner
                discussionPublicId={discussionPublicId}
                onClose={() => onOpenChange(false)}
                onCreateGroup={() => {
                    onOpenChange(false);
                    if (typeof window !== "undefined") {
                        window.setTimeout(() => {
                            showCreateGroupDialog(localizer);
                        }, 0);
                    }
                }}
                localizer={localizer}
                isPrivateDiscussion={options?.isPrivateDiscussion}
                isDiscussionAuthor={options?.isDiscussionAuthor}
            />
        </SimpleDialog>
    ));
}

/**
 * Draft-mode group picker launched from the inline publish-settings panel under the preview card.
 * Selections are persisted against the draft (`draftPublicId`) and survive close/reopen and the
 * scheduled-publish handoff, matching the draft-backed publish-target contract.
 */
export function showDraftPublishTargetsGroupsDialog(
    draftPublicId: string,
    localizer: Localizer,
    onPublishTargetCountsChange?: (counts: DiscussionDraftPublishTargetCounts) => void,
    languageTag?: string,
    flushPendingSave?: () => Promise<unknown>,
) {
    openSimpleDialog(({ open, onOpenChange }) => (
        <SimpleDialog
            open={open}
            onOpenChange={onOpenChange}
            title={<div id="groups-dialog-title">{localizer("create.discussion.publish_targets.groups.choose")}</div>}
            description={localizer("create.discussion.publish_targets.picker.description")}
            width="xl"
        >
            <AddDiscussionToGroupsDialogInner
                mode="draftPublishTargets"
                draftPublicId={draftPublicId}
                onClose={() => onOpenChange(false)}
                onCreateGroup={() => {
                    onOpenChange(false);
                    if (typeof window !== "undefined") {
                        window.setTimeout(() => {
                            showCreateGroupDialog(localizer);
                        }, 0);
                    }
                }}
                localizer={localizer}
                onPublishTargetCountsChange={onPublishTargetCountsChange}
                discussionLanguageTag={languageTag}
                flushPendingSave={flushPendingSave}
            />
        </SimpleDialog>
    ));
}
